import {
  BaseColorType,
  color,
  getColorsFromBaseColor,
  ThemeColors,
} from '@evrekadev/evreka-ui-components'
import React, { useEffect, useMemo, useState } from 'react'
import Chart from 'react-apexcharts'
import styled from 'styled-components'
import { WidgetSize } from '../types'
import { ApexOptions } from 'apexcharts'

export type LineGraphProps = {
  data?: Array<{
    date: string
    info: number
  }>
  color?: BaseColorType
  loading?: boolean
  fetching?: boolean
  showZero?: boolean
  unit?: string
  size: WidgetSize
}

export const LineGraph: React.FC<LineGraphProps> = ({
  data,
  color,
  showZero,
  loading = false,
  fetching = false,
  unit,
  size,
}) => {
  const colorToUse = ThemeColors[getColorsFromBaseColor(color ?? 'green').border]
  const average = data ? data.reduce((acc, curr) => acc + curr.info, 0) / data?.length : 0
  const numbers = useRandomNumbers(loading)

  const dataParsed = useMemo(() => {
    if (data) {
      return data
        .filter(({ info }) => (showZero ? true : info))
        .map(({ date, info }) => ({
          x: new Date(date).getTime(),
          y: info,
        }))
    }
    return []
  }, [data, showZero])

  const dataToRender = fetching
    ? [{ name: '', data: [] }]
    : loading
      ? [
          {
            name: '',
            data: numbers.map((n, i) => ({
              x: i,
              y: n,
            })),
          },
        ]
      : [{ name: '', data: dataParsed }]

  const options = getStaticOptions({ colorToUse, average, unit })

  return (
    <ChartStyled height={size === '2x2' ? '160' : '120'} series={dataToRender} options={options} />
  )
}

export const useRandomNumbers = (active: boolean, count = 10) => {
  const generateRandomNumbers = () => {
    return Array.from({ length: count }, () => Math.floor(Math.random() * 100))
  }

  const [numbers, setNumbers] = useState<number[]>(generateRandomNumbers())

  useEffect(() => {
    if (active) {
      setNumbers(generateRandomNumbers())

      const intervalId = setInterval(() => {
        setNumbers(generateRandomNumbers())
      }, 800)

      return () => clearInterval(intervalId)
    }
  }, [active])

  return numbers
}

const ChartStyled = styled(Chart)`
  /* uncomment to debug tooltip */
  /* .apexcharts-tooltip {
    opacity: 1 !important;
  } */

  .line-chart-tooltip {
    border-color: ${color('grey300')} !important;
    & > * {
      color: ${color('grey700')} !important;
      border-color: ${color('grey300')} !important;
    }
    & > .apexcharts-tooltip-title {
      background-color: ${color('grey100')} !important;
    }
    .apexcharts-tooltip-marker {
      margin-right: 0.125rem !important;
    }
  }
`

const getStaticOptions = (args: {
  colorToUse: string
  average: number
  unit?: string
}): ApexOptions => ({
  chart: {
    type: 'line',
    background: 'transparent',
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  grid: {
    // disable grid completely
    show: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2,
    colors: [args.colorToUse],
  },
  xaxis: {
    // disable x-axis completely
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      show: false,
    },
    // disable tooltip on x-axis
    tooltip: {
      enabled: false,
    },
  },
  yaxis: {
    // disable y-axis completely
    show: false,
  },
  markers: {
    size: 0,
    colors: args.colorToUse,
  },
  tooltip: {
    enabled: true,
    marker: {
      fillColors: [args.colorToUse],
    },
    x: {
      formatter: (value: number) => {
        return new Date(value).toDateString()
      },
    },
    y: {
      formatter: (value: number) => {
        return args.unit ? `${value} ${args.unit}` : value.toString()
      },
    },
    shared: true,
    cssClass: 'line-chart-tooltip',
  },
  annotations: {
    yaxis: [
      {
        y: args.average.toFixed(0),
        borderColor: args.colorToUse,
        strokeDashArray: 4,
        label: {
          offsetY: 6,
          text: 'Avg: ' + args.average?.toFixed(1),
          borderWidth: 0,
          style: {
            color: args.colorToUse,
            fontSize: '12px',
          },
        },
      },
    ],
  },
})
